import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, HomeIcon } from 'lucide-react';
import { adjacentSections, sectionByPath } from '../data/sections';
type PageShellProps = {
  path: string;
  children: React.ReactNode;
};
export function PageShell({
  path,
  children
}: PageShellProps) {
  const section = sectionByPath(path);
  const {
    previous,
    next
  } = adjacentSections(path);
  if (!section) return null;
  return <div className="bg-white" data-id="element-29" data-mp="2qtilz1lgk9">
      <header className="border-b border-white/10 bg-navy-900 pb-14 pt-28 lg:pb-16 lg:pt-36" data-id="element-30" data-mp="51226tdlpl0w">
        <div className="mx-auto w-full max-w-7xl px-6 lg:px-10" data-id="element-31" data-mp="3m97c92ycf025">
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.24em] text-gold-400" data-id="element-32" data-mp="3m8xjj7lwyesr">
            Section {section.number} of 09
          </p>
          <h1 className="mt-4 max-w-4xl font-display text-4xl font-extrabold leading-[1.05] tracking-tightest text-white sm:text-5xl" data-id="element-33" data-mp="2atiklya59cv0">
            {section.name}
          </h1>
          <p className="mt-5 max-w-2xl border-l-2 border-gold-500 pl-5 font-sans text-base leading-relaxed text-platinum-300 lg:text-lg" data-id="element-34" data-mp="db8n722iv50t">
            {section.summary}
          </p>
          <nav aria-label="On this page" className="mt-8" data-id="element-35" data-mp="8e8bobmwla2b">
            <ul className="flex flex-wrap gap-2" data-id="element-36" data-mp="mcpxu4x5uei4">
              {section.contains.map(topic => <li key={topic.label} data-id="element-37" data-mp="hc295pwem9r2">
                  <a href={topic.anchor} className="inline-flex whitespace-nowrap border border-white/20 px-3 py-1.5 font-sans text-xs font-semibold text-platinum-200 transition-colors duration-150 ease-out hover:border-gold-500 hover:bg-gold-500 hover:text-navy-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold-400" data-id="element-38" data-mp="hf6j73wjvzpn">
                    {topic.label}
                  </a>
                </li>)}
            </ul>
          </nav>
        </div>
      </header>

      <main data-id="element-39" data-mp="19zekf3x46v52">{children}</main>

      <nav aria-label="Section navigation" className="border-t border-platinum-200 bg-platinum-100" data-id="element-40" data-mp="7ocxjg15y3g8">
        <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-px bg-platinum-200 sm:grid-cols-2" data-id="element-41" data-mp="bowsccy9kuz7">
          {previous ? <Link to={previous.path} className="group flex items-center gap-4 bg-platinum-100 px-6 py-8 transition-colors duration-150 ease-out hover:bg-white lg:px-10" data-id="element-42" data-mp="gnghem85mmvs">
              <ArrowLeftIcon className="h-5 w-5 shrink-0 text-gold-600 transition-transform duration-200 ease-smooth group-hover:-translate-x-1" aria-hidden="true" data-id="element-43" data-mp="qgoxxx89aunq" />
              <span className="min-w-0" data-id="element-44" data-mp="zdfzuq9hp3q8">
                <span className="block font-sans text-xs font-semibold uppercase tracking-[0.16em] text-charcoal-600" data-id="element-45" data-mp="14dath1kng7ez">
                  Previous · Section {previous.number}
                </span>
                <span className="mt-1 block font-display text-base font-bold text-navy-900" data-id="element-46" data-mp="366rz5zp7yy73">
                  {previous.name}
                </span>
              </span>
            </Link> : <Link to="/" className="group flex items-center gap-4 bg-platinum-100 px-6 py-8 transition-colors duration-150 ease-out hover:bg-white lg:px-10" data-id="element-47" data-mp="2tug4elm3awcx">
              <HomeIcon className="h-5 w-5 shrink-0 text-gold-600" aria-hidden="true" data-id="element-48" data-mp="2jycrqltm3fbq" />
              <span className="min-w-0" data-id="element-49" data-mp="3hlbv1fet7lil">
                <span className="block font-sans text-xs font-semibold uppercase tracking-[0.16em] text-charcoal-600" data-id="element-50" data-mp="3hkae4ub444sy">
                  Previous · Introduction
                </span>
                <span className="mt-1 block font-display text-base font-bold text-navy-900" data-id="element-51" data-mp="37lb4p9iiw54w">
                  Executive Hero &amp; Board Positioning
                </span>
              </span>
            </Link>}

          {next ? <Link to={next.path} className="group flex items-center justify-end gap-4 bg-platinum-100 px-6 py-8 text-right transition-colors duration-150 ease-out hover:bg-white lg:px-10" data-id="element-52" data-mp="1s65fdxl52p35">
              <span className="min-w-0" data-id="element-53" data-mp="3kulh6j52cwmi">
                <span className="block font-sans text-xs font-semibold uppercase tracking-[0.16em] text-charcoal-600" data-id="element-54" data-mp="3kvmuyvdgrx7x">
                  Next · Section {next.number}
                </span>
                <span className="mt-1 block font-display text-base font-bold text-navy-900" data-id="element-55" data-mp="3b40821rrk9pc">
                  {next.name}
                </span>
              </span>
              <ArrowRightIcon className="h-5 w-5 shrink-0 text-gold-600 transition-transform duration-200 ease-smooth group-hover:translate-x-1" aria-hidden="true" data-id="element-56" data-mp="7lyvjfbcqasu" />
            </Link> : <Link to="/" className="group flex items-center justify-end gap-4 bg-platinum-100 px-6 py-8 text-right transition-colors duration-150 ease-out hover:bg-white lg:px-10" data-id="element-57" data-mp="3twb53ezqldfj">
              <span className="min-w-0" data-id="element-58" data-mp="2avaa5lyrde9c">
                <span className="block font-sans text-xs font-semibold uppercase tracking-[0.16em] text-charcoal-600" data-id="element-59" data-mp="2au8xxe60alq1">
                  Return to Introduction
                </span>
                <span className="mt-1 block font-display text-base font-bold text-navy-900" data-id="element-60" data-mp="2ktzs1eqnmypl">
                  Executive Hero &amp; Board Positioning
                </span>
              </span>
              <HomeIcon className="h-5 w-5 shrink-0 text-gold-600" aria-hidden="true" data-id="element-61" data-mp="1s49fha4o84vl" />
            </Link>}
        </div>
      </nav>
    </div>;
}